/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { HiArrowUturnDown, HiArrowPath } from "react-icons/hi2"; // Npm: react-icons library.
import { Flex, Text, Button, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import DownloadToExcel from "components/DownloadToExcel";
import CustomerReportDropDownOptions from "components/CustomerReportDropDownOptions";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import CustomerReadQuery from "./__query__/index.customer.read.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [sortModel, setSortModel] = React.useState([]);

  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : null;

  const _QueryCustomerRead = useQuery(CustomerReadQuery, {
    variables: {
      skip: paginationModel?.pageSize * paginationModel?.page,
      take: paginationModel?.pageSize,
      filter: _appliedFilter,
      smsStartDate: dateRange?.selection.startDate,
      smsEndDate: dateRange?.selection.endDate,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
  });

  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        filterable: true,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        filterable: true,
        hide: true,
      },
      {
        field: "Company.displayName",
        headerName: "Company Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        flex: 1,
        headerAlign: "center",
        minWidth: 200,
      },
      {
        field: "type",
        headerName: "Customer Type",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        minWidth: 160,
        type: "singleSelect",
        valueOptions: _.without(
          Object.React.App.enums.CUSTOMER_TYPE.enums?.map((i) => i?.key),
          "TERMINATION",
        ),
      },
      {
        field: "totalSms",
        headerName: "Total Sms",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "smsDelivered",
        headerName: "Sms Delivered",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsFailed",
        headerName: "Sms Failed",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "smsPending",
        headerName: "Sms Pending",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsUndelivered",
        headerName: "Sms Undelivered",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "dlrPercentage",
        headerName: "Dlr Percentage",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <CustomerReportDropDownOptions
            customerId={row?.customerId}
            companyName={row["Company.displayName"]}
            type={row?.type}
            currency={row?.currency}
          />
        ),
      },
    ],
    [],
  );

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryCustomerRead?.data?.CustomerRead?.map((r, index) =>
          "READ_SUCCESSFUL" === r.status
            ? {
                id: r?.id || String.random(9),
                customerId: r?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                "Company.displayName": r?.Company?.displayName ?? "-",
                type: r?.type ?? "-",
                totalSms: r?._smsCountForGivenCustomer?.[r?.id]?.smsCount ?? 0,
                smsDelivered:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsDelivered ?? 0,
                smsFailed:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsFailed ?? 0,
                smsPending:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsAccepted ?? 0,
                smsUndelivered:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsUndelivered ?? 0,
                dlrPercentage: !r?._smsCountForGivenCustomer?.[r?.id]?.smsCount
                  ? "0%"
                  : `${(
                      ((r?._smsCountForGivenCustomer?.[r?.id]?.smsDelivered ||
                        0) /
                        (r?._smsCountForGivenCustomer?.[r?.id]?.smsCount ||
                          0)) *
                      100
                    )?.toFixed(2)}%`,
                currency: r?.Billing?.currency,
              }
            : null,
        ),
      ),
    [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page],
  );

  // Return component.
  return (
    <>
      <Flex className="reportsCustomer base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="83vh"
          maxH="83vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap="12px"
          >
            <Flex
              flex={_isCurrentViewMobile ? 1 : 0.6}
              gap="12px"
              flexDir="column"
            >
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date"}
                .
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              flex={_isCurrentViewMobile ? 1 : 0.4}
              justifyContent="flex-end"
              gap="12px"
              alignItems="center"
            >
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={() => _QueryCustomerRead.refetch()}
                isDisabled={_QueryCustomerRead?.loading}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_QueryCustomerRead?.data?.CustomerRead?.map(
                  (item, __index) =>
                    "READ_SUCCESSFUL" === item.status
                      ? {
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Company Name": item?.Company?.displayName ?? "-",
                          "Open SID":
                            _.compact(
                              _.flatten(
                                _.pluck(
                                  _.flatten(
                                    _.pluck(item.CustomerAccount, "Smpp"),
                                  ),
                                  "sourceAddress",
                                ),
                              ),
                            )?.join(", ") ?? "-",
                          "Total Sms":
                            item?._smsCountForGivenCustomer?.[item?.id]
                              ?.smsCount ?? "-",
                          "Sms Delivered":
                            item?._smsCountForGivenCustomer?.[item?.id]
                              ?.smsDelivered ?? "-",
                          "Sms Failed":
                            item?._smsCountForGivenCustomer?.[item?.id]
                              ?.smsFailed ?? "-",
                          "Sms Pending":
                            item?._smsCountForGivenCustomer?.[item?.id]
                              ?.smsAccepted ?? "-",
                          "Sms Undelivered":
                            item?._smsCountForGivenCustomer?.[item?.id]
                              ?.smsUndelivered ?? "-",
                          "Dlr Percentage": `${
                            (
                              (item?._smsCountForGivenCustomer?.[item?.id]
                                ?.smsDelivered /
                                item?._smsCountForGivenCustomer?.[item?.id]
                                  ?.smsCount) *
                              100
                            )?.toFixed(2) ?? "-"
                          }%`,
                        }
                      : void 0,
                )}
                headersData={[
                  "S.No.",
                  "Company Name",
                  "Open SID",
                  "Total Sms",
                  "Sms Delivered",
                  "Sms Failed",
                  "Sms Pending",
                  "Sms Undelivered",
                  "Dlr Percentage",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={
              _.first(_QueryCustomerRead?.data?.CustomerRead)?._totalCount ?? 0
            }
            paginationMode="server"
            filterMode="server"
            sortMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterModel={filterModel}
            onFilterModelChange={_setFilterDebounced}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryCustomerRead?.loading}
          />
        </Flex>
      </Flex>
    </>
  );
};

/*
 * EXPORTS
 */
export default Index;
